import {
    FormEventHandler,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
    GeolocateControl,
    Layer,
    Map as MapView,
    Marker,
    NavigationControl,
    Source,
    type MapMouseEvent,
    type MapRef
} from 'react-map-gl/mapbox';
import type { FeatureCollection, Polygon } from 'geojson';
import type MapboxDraw from '@mapbox/mapbox-gl-draw';
import { GeoAlertPointRequest, UpdateDeviceConfigGeoAlertRequest } from '@/api';
import {
    UIButton,
    UIFormInput,
    UIIcon,
    UIInlineAlert,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISelectInput,
    UISelectValue,
    UISwitch,
    UIText
} from '@/ui';
import { useBulkUpdateDeviceGeoAlertAction } from '../actions';
import { useBulkAlertLicenses } from '../queries';
import { useDevicesTranslation } from '../hooks';
import { MAPBOX_TOKEN } from '@/config';
import { blue, charcoal, concrete, fontColorLight, white } from '@/ui/styles';
import { circleRing, LngLat } from '../util/geofence-geometry';
import { GeofenceDrawControl } from './geofence-draw-control';
import { PendingConfigNote } from './pending-config-note';

const MAPBOX_STYLE = 'mapbox://styles/mapbox/streets-v12';
const GEOFENCE_SLOT_COUNT = 4;

type Shape = 'circle' | 'polygon';
type Direction = 'in' | 'out';

type GeocodeFeature = {
    id: string;
    place_name: string;
    center: [number, number];
    bbox?: [number, number, number, number];
};

export type BulkGeofenceModalProps = {
    companyId: string;
    deviceIds: string[];
    isOpen: boolean;
    onClose: () => void;
    onApplied: () => void;
};

export function BulkGeofenceModal(props: BulkGeofenceModalProps): ReactElement {
    const { isOpen, onClose } = props;
    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen ? <FormContent {...props} /> : null}
        </UIModal>
    );
}

function FormContent(props: BulkGeofenceModalProps): ReactElement {
    const { companyId, deviceIds, onClose, onApplied } = props;
    const { t } = useDevicesTranslation();
    const { bulkUpdateDeviceGeoAlert, loading } =
        useBulkUpdateDeviceGeoAlertAction({ companyId });
    const licenses = useBulkAlertLicenses({ companyId, deviceIds });

    const slotOptions: UISelectValue[] = Array.from(
        { length: GEOFENCE_SLOT_COUNT },
        (_, index) => ({
            value: String(index),
            label: t('shared.devices.geofence_slot_label', {
                number: index + 1
            })
        })
    );

    const [slot, setSlot] = useState<UISelectValue>(slotOptions[0]);
    const [validationError, setValidationError] = useState('');
    const [shape, setShape] = useState<Shape>('circle');
    const [direction, setDirection] = useState<Direction>('in');
    const [enabled, setEnabled] = useState(true);
    const [radius, setRadius] = useState('100');
    const [centre, setCentre] = useState<LngLat | null>(null);
    const [polygonCount, setPolygonCount] = useState(0);

    const drawRef = useRef<MapboxDraw | null>(null);
    const [drawReady, setDrawReady] = useState(false);

    const mapRef = useRef<MapRef | null>(null);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<GeocodeFeature[]>([]);
    const [searching, setSearching] = useState(false);
    const skipSearchRef = useRef(false);

    const blocked = enabled && licenses.geo.toEnable > licenses.geo.free;

    const handleDrawReady = useCallback((draw: MapboxDraw | null) => {
        drawRef.current = draw;
        setDrawReady(draw !== null);
    }, []);

    const handleDrawChange = useCallback(() => {
        const draw = drawRef.current;
        if (!draw) return;
        const polys = draw
            .getAll()
            .features.filter((f) => f.geometry.type === 'Polygon');
        polys.slice(0, -1).forEach((f) => {
            if (f.id !== undefined) draw.delete(String(f.id));
        });
        const last = polys[polys.length - 1];
        const ring =
            last && last.geometry.type === 'Polygon'
                ? (last.geometry.coordinates[0] ?? [])
                : [];
        setPolygonCount(Math.max(0, ring.length - 1));
    }, []);

    useEffect(() => {
        if (shape !== 'polygon' || !drawReady) return;
        const draw = drawRef.current;
        if (!draw) return;
        draw.deleteAll();
        setPolygonCount(0);
        draw.changeMode('draw_polygon');
    }, [shape, drawReady]);

    const radiusValue = Number(radius);

    const circleData = useMemo<FeatureCollection<Polygon> | null>(() => {
        if (shape !== 'circle' || !centre || !(radiusValue > 0)) return null;
        return {
            type: 'FeatureCollection',
            features: [
                {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'Polygon',
                        coordinates: [circleRing(centre, radiusValue)]
                    }
                }
            ]
        };
    }, [shape, centre, radiusValue]);

    // Debounced place search.
    useEffect(() => {
        if (skipSearchRef.current) {
            skipSearchRef.current = false;
            return;
        }
        const q = query.trim();
        if (!q || !MAPBOX_TOKEN) {
            setResults([]);
            return;
        }
        const handle = setTimeout(() => {
            setSearching(true);
            fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
                    q
                )}.json?limit=5&access_token=${MAPBOX_TOKEN}`
            )
                .then(
                    (res) =>
                        res.json() as Promise<{ features?: GeocodeFeature[] }>
                )
                .then((data) => setResults(data.features ?? []))
                .catch(() => setResults([]))
                .finally(() => setSearching(false));
        }, 300);
        return () => clearTimeout(handle);
    }, [query]);

    function flyToResult(feature: GeocodeFeature) {
        const map = mapRef.current?.getMap();
        if (map) {
            if (feature.bbox) {
                map.fitBounds(
                    [
                        [feature.bbox[0], feature.bbox[1]],
                        [feature.bbox[2], feature.bbox[3]]
                    ],
                    { padding: 40, duration: 800, maxZoom: 16 }
                );
            } else {
                map.flyTo({ center: feature.center, zoom: 14, duration: 800 });
            }
        }
        skipSearchRef.current = true;
        setQuery(feature.place_name);
        setResults([]);
    }

    function clearShape() {
        if (shape === 'circle') {
            setCentre(null);
            return;
        }
        const draw = drawRef.current;
        if (!draw) return;
        draw.deleteAll();
        draw.changeMode('draw_polygon');
        setPolygonCount(0);
    }

    function buildGeometry():
        | { shape: 'circle'; radius: number; points: GeoAlertPointRequest[] }
        | { shape: 'polygon'; points: GeoAlertPointRequest[] }
        | null {
        if (shape === 'circle') {
            if (
                !centre ||
                !Number.isFinite(radiusValue) ||
                radiusValue <= 0 ||
                radiusValue > 65535
            ) {
                return null;
            }
            return {
                shape: 'circle',
                radius: Math.round(radiusValue),
                points: [
                    { latitude: centre.latitude, longitude: centre.longitude }
                ]
            };
        }
        const poly = drawRef.current
            ?.getAll()
            .features.find((f) => f.geometry.type === 'Polygon');
        if (!poly || poly.geometry.type !== 'Polygon') return null;
        let ring = poly.geometry.coordinates[0] as [number, number][];
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (ring.length > 1 && first[0] === last[0] && first[1] === last[1]) {
            ring = ring.slice(0, -1);
        }
        if (ring.length < 3 || ring.length > 15) return null;
        return {
            shape: 'polygon',
            points: ring.map(([longitude, latitude]) => ({
                latitude,
                longitude
            }))
        };
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();
        setValidationError('');

        if (blocked) {
            return;
        }

        const geometry = buildGeometry();
        if (enabled && !geometry) {
            setValidationError(
                shape === 'circle'
                    ? t('shared.fields.geofence_shape.error_circle')
                    : t('shared.fields.geofence_shape.error_polygon')
            );
            return;
        }

        const data: UpdateDeviceConfigGeoAlertRequest = geometry
            ? {
                  enabled,
                  direction,
                  shape: geometry.shape,
                  points: geometry.points,
                  radius:
                      geometry.shape === 'circle'
                          ? (geometry.radius as unknown as object)
                          : null
              }
            : { enabled, direction, shape: null, points: null };

        bulkUpdateDeviceGeoAlert({
            ...data,
            deviceIds,
            slot: Number(slot.value)
        })
            .then(() => onApplied())
            .catch(() => {}); // Error handled in the action
    };

    const shapeOptions = [
        { label: t('shared.devices.shape_circle'), value: 'circle' },
        { label: t('shared.devices.shape_polygon'), value: 'polygon' }
    ];
    const directionOptions = [
        {
            label: t('shared.devices.geofence_direction_in_option'),
            value: 'in'
        },
        {
            label: t('shared.devices.geofence_direction_out_option'),
            value: 'out'
        }
    ];

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UIModalHeader
                title={t('components.bulk_geofence_modal.title')}
                subtitle={
                    deviceIds.length > 1
                        ? t('shared.devices.bulk_applies_to', {
                              count: deviceIds.length
                          })
                        : undefined
                }
            />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16
                    }}
                >
                    <PendingConfigNote />

                    {validationError && (
                        <UIInlineAlert text={validationError} />
                    )}
                    {!licenses.loading && blocked && (
                        <UIInlineAlert
                            text={t(
                                'components.bulk_geofence_modal.not_enough_licences',
                                {
                                    count:
                                        licenses.geo.toEnable -
                                        licenses.geo.free
                                }
                            )}
                        />
                    )}

                    <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ flex: 1 }}>
                            <UISelectInput
                                label={t(
                                    'components.bulk_geofence_modal.slot_select_label'
                                )}
                                value={slot}
                                options={slotOptions}
                                clearable={false}
                                onChange={(option) => {
                                    if (option) setSlot(option);
                                }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <UISelectInput
                                label={t('shared.devices.shape_label')}
                                value={shapeOptions.find(
                                    (o) => o.value === shape
                                )}
                                options={shapeOptions}
                                clearable={false}
                                onChange={(option) => {
                                    if (option) setShape(option.value as Shape);
                                }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <UISelectInput
                                label={t(
                                    'shared.devices.geofence_direction_label'
                                )}
                                value={directionOptions.find(
                                    (o) => o.value === direction
                                )}
                                options={directionOptions}
                                clearable={false}
                                onChange={(option) => {
                                    if (option)
                                        setDirection(option.value as Direction);
                                }}
                            />
                        </div>
                        {shape === 'circle' && (
                            <div style={{ flex: 1 }}>
                                <UIFormInput
                                    label={t(
                                        'shared.fields.geofence_radius.label'
                                    )}
                                    type="number"
                                    value={radius}
                                    onChange={setRadius}
                                />
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12
                        }}
                    >
                        <UISwitch checked={enabled} onChange={setEnabled} />
                        <UIText>
                            {t('shared.devices.geofence_enabled_label')}
                        </UIText>
                    </div>

                    {MAPBOX_TOKEN ? (
                        <div
                            style={{
                                position: 'relative',
                                height: 400,
                                width: '100%',
                                overflow: 'hidden',
                                borderRadius: 8,
                                border: `1px solid ${concrete}`
                            }}
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    zIndex: 1,
                                    width: 280
                                }}
                            >
                                <SearchBox
                                    query={query}
                                    searching={searching}
                                    results={results}
                                    placeholder={t(
                                        'shared.devices.geofence_search_placeholder'
                                    )}
                                    onQueryChange={setQuery}
                                    onClear={() => {
                                        setQuery('');
                                        setResults([]);
                                    }}
                                    onSelect={flyToResult}
                                />
                            </div>
                            <MapView
                                ref={mapRef}
                                mapboxAccessToken={MAPBOX_TOKEN}
                                mapStyle={MAPBOX_STYLE}
                                initialViewState={{
                                    longitude: 0,
                                    latitude: 20,
                                    zoom: 1.4
                                }}
                                style={{ width: '100%', height: '100%' }}
                                onClick={(e: MapMouseEvent) => {
                                    if (shape !== 'circle') return;
                                    setCentre({
                                        longitude: e.lngLat.lng,
                                        latitude: e.lngLat.lat
                                    });
                                }}
                                cursor={shape === 'circle' ? 'crosshair' : ''}
                            >
                                <NavigationControl
                                    position="top-right"
                                    showCompass={false}
                                />
                                <GeolocateControl
                                    position="top-right"
                                    trackUserLocation={false}
                                />
                                {shape === 'polygon' && (
                                    <GeofenceDrawControl
                                        onReady={handleDrawReady}
                                        onChange={handleDrawChange}
                                    />
                                )}
                                {shape === 'circle' && circleData && (
                                    <Source
                                        id="bulk-geofence-circle"
                                        type="geojson"
                                        data={circleData}
                                    >
                                        <Layer
                                            id="bulk-geofence-circle-fill"
                                            type="fill"
                                            paint={{
                                                'fill-color': blue,
                                                'fill-opacity': 0.15
                                            }}
                                        />
                                        <Layer
                                            id="bulk-geofence-circle-line"
                                            type="line"
                                            paint={{
                                                'line-color': blue,
                                                'line-width': 2
                                            }}
                                        />
                                    </Source>
                                )}
                                {shape === 'circle' && centre && (
                                    <Marker
                                        longitude={centre.longitude}
                                        latitude={centre.latitude}
                                        draggable
                                        onDrag={(e) =>
                                            setCentre({
                                                longitude: e.lngLat.lng,
                                                latitude: e.lngLat.lat
                                            })
                                        }
                                    >
                                        <div
                                            style={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: '50%',
                                                backgroundColor: blue,
                                                border: `2px solid ${white}`,
                                                boxShadow:
                                                    '0 1px 4px rgba(0,0,0,0.4)'
                                            }}
                                        />
                                    </Marker>
                                )}
                            </MapView>
                        </div>
                    ) : (
                        <UIInlineAlert
                            text={t('shared.devices.geofence_no_token')}
                        />
                    )}

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12
                        }}
                    >
                        <UIText variant="small" type="default2">
                            {shape === 'circle'
                                ? centre
                                    ? t(
                                          'shared.devices.geofence_circle_hint_move',
                                          {
                                              lat: centre.latitude.toFixed(5),
                                              lng: centre.longitude.toFixed(5)
                                          }
                                      )
                                    : t(
                                          'shared.devices.geofence_circle_hint_place'
                                      )
                                : t('shared.devices.geofence_polygon_hint', {
                                      value: polygonCount
                                  })}
                        </UIText>
                        {((shape === 'polygon' && polygonCount > 0) ||
                            (shape === 'circle' && centre)) && (
                            <UIButton
                                type="button"
                                variant="textDefault"
                                text={t('shared.labels.clear')}
                                onClick={clearShape}
                            />
                        )}
                    </div>
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        text: t('shared.labels.apply'),
                        loading: loading,
                        disabled: loading || blocked || licenses.loading
                    },
                    {
                        text: t('shared.labels.cancel'),
                        type: 'button',
                        disabled: loading,
                        onClick: onClose
                    }
                ]}
            />
        </form>
    );
}

function SearchBox(props: {
    query: string;
    searching: boolean;
    results: GeocodeFeature[];
    placeholder: string;
    onQueryChange: (value: string) => void;
    onClear: () => void;
    onSelect: (feature: GeocodeFeature) => void;
}): ReactElement {
    const {
        query,
        searching,
        results,
        placeholder,
        onQueryChange,
        onClear,
        onSelect
    } = props;
    const { t } = useDevicesTranslation();
    return (
        <div>
            <div style={{ position: 'relative' }}>
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: 8,
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        display: 'flex'
                    }}
                >
                    <UIIcon name="search" size={16} color={fontColorLight} />
                </div>
                <input
                    value={query}
                    onChange={(e) => onQueryChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (results[0]) onSelect(results[0]);
                        } else if (e.key === 'Escape') {
                            e.preventDefault();
                            e.stopPropagation();
                            onClear();
                        }
                    }}
                    placeholder={placeholder}
                    style={{
                        width: '100%',
                        height: 36,
                        padding: '0 32px',
                        borderRadius: 8,
                        border: `1px solid ${concrete}`,
                        backgroundColor: white,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                        outline: 'none',
                        boxSizing: 'border-box'
                    }}
                />
                {query && (
                    <button
                        type="button"
                        onClick={onClear}
                        aria-label={t('shared.labels.clear_search')}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            right: 8,
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            display: 'flex'
                        }}
                    >
                        <UIIcon name="x" size={16} color={charcoal} />
                    </button>
                )}
            </div>
            {results.length > 0 && (
                <div
                    style={{
                        marginTop: 4,
                        borderRadius: 8,
                        border: `1px solid ${concrete}`,
                        backgroundColor: white,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        overflow: 'hidden'
                    }}
                >
                    {results.map((feature) => (
                        <button
                            key={feature.id}
                            type="button"
                            onClick={() => onSelect(feature)}
                            style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                padding: '8px 12px',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            <UIText variant="small">
                                {feature.place_name}
                            </UIText>
                        </button>
                    ))}
                </div>
            )}
            {searching && (
                <div style={{ marginTop: 4 }}>
                    <UIText variant="small" type="default2">
                        …
                    </UIText>
                </div>
            )}
        </div>
    );
}
